/**
 * Probability Timeline Chart
 * Visual representation of probability over time
 */

import React from 'react';
import { Box, Typography, Sheet, Tooltip } from '@mui/joy';
import type { ProbabilityWindow } from '../../astro/probability/eventProbabilityEngine';

interface ProbabilityTimelineChartProps {
  windows: ProbabilityWindow[];
  yearsToShow: number;
  /** Compact mode: smaller viewBox for better legibility on mobile screens */
  compact?: boolean;
}

export default function ProbabilityTimelineChart({
  windows,
  yearsToShow,
  compact = false,
}: ProbabilityTimelineChartProps) {
  if (windows.length === 0) return null;

  // Create month-based data points for smooth graph
  const today = new Date();
  const monthsToShow = yearsToShow * 12;
  const dataPoints: Array<{ month: number; probability: number; date: Date }> = [];

  for (let month = 0; month < monthsToShow; month++) {
    const monthStart = new Date(today);
    monthStart.setMonth(today.getMonth() + month);
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);

    const monthEnd = new Date(monthStart);
    monthEnd.setMonth(monthEnd.getMonth() + 1);
    monthEnd.setDate(0); // Last day of month
    monthEnd.setHours(23, 59, 59, 999);

    const monthStartTime = monthStart.getTime();
    const monthEndTime = monthEnd.getTime();

    // Find windows that overlap with this month
    const matchingWindows = windows.filter((w) => {
      const wStart = w.startDate.getTime();
      const wEnd = w.endDate.getTime();
      // Check for overlap: window starts before month ends AND window ends after month starts
      return wStart <= monthEndTime && wEnd >= monthStartTime;
    });

    // Use the highest probability if multiple windows overlap this month
    const maxProbability =
      matchingWindows.length > 0
        ? Math.max(...matchingWindows.map((w) => w.probability))
        : 0;

    dataPoints.push({
      month,
      probability: maxProbability,
      date: monthStart,
    });
  }

  // Floor at 50 (not 75) — with base=0 scoring, realistic peaks are 45–75%
  const maxProbability = Math.max(...dataPoints.map((d) => d.probability), 50);
  // Compact mode: smaller viewBox so on mobile (~310px wide) scale ≈ 74% instead of 39%
  const chartWidth = compact ? 420 : 800;
  const chartHeight = compact ? 250 : 200;
  const padding = compact
    ? { top: 24, right: 18, bottom: 46, left: 52 }
    : { top: 20, right: 20, bottom: 40, left: 50 };
  const dotRadius = compact ? 5 : 5;
  const dotLabelFontSize = compact ? 12 : 10;
  const axisLabelFontSize = compact ? 14 : 12;
  const tickFontSize = compact ? 13 : 11;
  const xAxisStep = compact ? 3 : 2;
  const graphWidth = chartWidth - padding.left - padding.right;
  const graphHeight = chartHeight - padding.top - padding.bottom;

  // Generate SVG path for probability curve
  const pathData = dataPoints
    .map((point, index) => {
      const x = padding.left + (index / (dataPoints.length - 1)) * graphWidth;
      const y = padding.top + graphHeight - (point.probability / maxProbability) * graphHeight;
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  // Generate area fill path
  const areaPath =
    pathData +
    ` L ${padding.left + graphWidth} ${padding.top + graphHeight}` +
    ` L ${padding.left} ${padding.top + graphHeight} Z`;

  // X-axis labels (every 2 months for better granularity)
  const xAxisLabels = dataPoints
    .filter((_, index) => index % xAxisStep === 0 || index === dataPoints.length - 1)
    .map((point) => {
      const x = padding.left + (point.month / (dataPoints.length - 1)) * graphWidth;
      const label = point.date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      return { x, label };
    });

  // Y-axis labels — step every 25% up to maxProbability
  const yAxisLabels = [0, 25, 50, 75, 100]
    .filter((val) => val <= maxProbability)
    .map((val) => {
      const y = padding.top + graphHeight - (val / maxProbability) * graphHeight;
      return { y, label: `${val}%` };
    });

  // Rating zones — boundaries match calculateCombinedProbability thresholds
  const zones = [
    { min: 60, max: 100, color: 'rgba(184,48,80,0.09)',    dotColor: '#b83050', label: 'Very High (60-100%)' },
    { min: 45, max: 60,  color: 'rgba(155,111,181,0.10)',  dotColor: '#9b6fb5', label: 'High (45-60%)' },
    { min: 28, max: 45,  color: 'rgba(200,137,26,0.09)',   dotColor: '#c8891a', label: 'Medium (28-45%)' },
    { min: 0,  max: 28,  color: 'rgba(160,160,160,0.07)', dotColor: '#aaaaaa', label: 'Low (0-28%)' },
  ];

  return (
    <Box sx={{ mb: 1 }}>
        {/* Responsive container - compact uses aspect-ratio trick so no empty space */}
        <Box sx={compact ? {
          width: '100%', position: 'relative',
          paddingBottom: `${((chartHeight / chartWidth) * 100).toFixed(2)}%`,
        } : { width: '100%' }}>
          <svg
            width="100%"
            height={compact ? '100%' : chartHeight}
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            preserveAspectRatio="xMidYMid meet"
            style={compact
              ? { display: 'block', position: 'absolute', top: 0, left: 0 }
              : { display: 'block', maxWidth: '100%' }}
          >
            {/* Background zones */}
            {zones
              .filter((zone) => zone.max <= maxProbability)
              .map((zone, index) => {
                const yTop = padding.top + graphHeight - (zone.max / maxProbability) * graphHeight;
                const yBottom = padding.top + graphHeight - (zone.min / maxProbability) * graphHeight;
                return (
                  <rect
                    key={index}
                    x={padding.left}
                    y={yTop}
                    width={graphWidth}
                    height={yBottom - yTop}
                    fill={zone.color}
                  />
                );
              })}

            {/* Grid lines (horizontal) */}
            {yAxisLabels.map((label, index) => (
              <line
                key={`grid-h-${index}`}
                x1={padding.left}
                y1={label.y}
                x2={padding.left + graphWidth}
                y2={label.y}
                stroke="#e0e0e0"
                strokeWidth="1"
                strokeDasharray="4 2"
              />
            ))}

            {/* Grid lines (vertical - every 2 months) */}
            {xAxisLabels.map((label, index) => (
              <line
                key={`grid-v-${index}`}
                x1={label.x}
                y1={padding.top}
                x2={label.x}
                y2={padding.top + graphHeight}
                stroke="#e0e0e0"
                strokeWidth="1"
                strokeDasharray="4 2"
              />
            ))}

            {/* Area fill under curve */}
            <path d={areaPath} fill="rgba(184,48,80,0.08)" />

            {/* Probability curve */}
            <path d={pathData} fill="none" stroke="#b83050" strokeWidth={compact ? 3.5 : 2.5} strokeLinecap="round" />

            {/* Data points with percentage labels */}
            {dataPoints
              .filter((point) => point.probability > 0)
              .map((point, index) => {
                const x = padding.left + (point.month / (dataPoints.length - 1)) * graphWidth;
                const y = padding.top + graphHeight - (point.probability / maxProbability) * graphHeight;
                
                // Color coding based on probability ranges — brand palette
                const color =
                  point.probability >= 60
                    ? '#b83050' // Rose-crimson — Very High
                    : point.probability >= 45
                    ? '#9b6fb5' // Lavender — High
                    : point.probability >= 28
                    ? '#c8891a' // Sri Yantra gold — Medium
                    : '#aaaaaa'; // Neutral — Low

                return (
                  <g key={index}>
                    {/* Circle node */}
                    <circle cx={x} cy={y} r={dotRadius} fill={color} stroke="white" strokeWidth="2">
                      <title>
                        {point.date.toLocaleDateString('en-US', {
                          month: 'short',
                          year: 'numeric',
                        })}
                        : {point.probability.toFixed(0)}%
                      </title>
                    </circle>
                    
                    {/* Percentage label above node */}
                    <text
                      x={x}
                      y={y - 14}
                      textAnchor="middle"
                      fontSize={dotLabelFontSize}
                      fontWeight="600"
                      fill={color}
                    >
                      {point.probability.toFixed(0)}%
                    </text>
                  </g>
                );
              })}

            {/* Y-axis */}
            <line
              x1={padding.left}
              y1={padding.top}
              x2={padding.left}
              y2={padding.top + graphHeight}
              stroke="#666"
              strokeWidth="2"
            />

            {/* X-axis */}
            <line
              x1={padding.left}
              y1={padding.top + graphHeight}
              x2={padding.left + graphWidth}
              y2={padding.top + graphHeight}
              stroke="#666"
              strokeWidth="2"
            />

            {/* Y-axis labels */}
            {yAxisLabels.map((label, index) => (
              <text
                key={`y-label-${index}`}
                x={padding.left - 10}
                y={label.y}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={axisLabelFontSize}
                fill="#666"
              >
                {label.label}
              </text>
            ))}

            {/* X-axis labels */}
            {xAxisLabels.map((label, index) => (
              <text
                key={`x-label-${index}`}
                x={label.x}
                y={padding.top + graphHeight + 20}
                textAnchor="middle"
                fontSize={tickFontSize}
                fill="#666"
              >
                {label.label}
              </text>
            ))}

            {/* Y-axis title */}
            <text
              x={15}
              y={chartHeight / 2}
              textAnchor="middle"
              fontSize={axisLabelFontSize}
              fill="#666"
              transform={`rotate(-90 15 ${chartHeight / 2})`}
            >
              Probability (%)
            </text>

            {/* X-axis title */}
            <text
              x={chartWidth / 2}
              y={chartHeight - 5}
              textAnchor="middle"
              fontSize={axisLabelFontSize}
              fill="#666"
            >
              Timeline
            </text>
          </svg>
        </Box>

        {/* Legend — outside the aspect-ratio box so it renders below, not over, the chart */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 1.25, flexWrap: 'wrap' }}>
          {zones.map((zone, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 10, height: 10, borderRadius: '50%',
                  bgcolor: zone.dotColor,
                  flexShrink: 0,
                }}
              />
              <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.65rem', letterSpacing: '0.01em' }}>
                {zone.label}
              </Typography>
            </Box>
          ))}
        </Box>
    </Box>
  );
}
